// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DefaultMapPropsDefinedSchema.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import {
  createPopupSettingSchema,
  createCheckBoxSchema,
  createSelectSchema,
  createNumberSchema,
  createColorPaletteSettingSchema,
  createOrientationSettingsSchema
} from 'share/utils/Izenda.PropsEditorGuiSchemaFunctions';

import ChartLegendSettings from 'modules/ReportDesigner/Fields/PropertiesBuilder/ChartSettings/Izenda.ChartLegendSettings';
import ChartBorderSettings from 'modules/ReportDesigner/Fields/PropertiesBuilder/ChartSettings/Izenda.ChartBorderSettings';
import ChartDataRefreshSettings from 'modules/ReportDesigner/Fields/PropertiesBuilder/Izenda.DataRefreshSetting';
import ZoomForMapSettings from 'modules/ReportDesigner/Fields/PropertiesBuilder/ChartSettings/Izenda.ZoomForMap';
import ChartBackgroundColorSettings from 'modules/ReportDesigner/Fields/PropertiesBuilder/ChartSettings/Izenda.ChartBackgroundColorSettings';

import { REPORT_PART_TYPE } from 'logic/data/Izenda.ReportDesignerConstants';
import { getAll } from 'share/factories/Izenda.ReportPartStyleFactories';
import localizer from 'share/localization/Izenda.Localizer';
import MapCollection from 'logic/data/Izenda.MapCollectionConstants';
import MapConstant from 'logic/data/Izenda.ReportPartMapConstants';
import { MESSAGE } from 'logic/data/Izenda.PropertiesBuilderConstants';

const getMapOptions = () =>
  getAll(REPORT_PART_TYPE.Map).map(({ label, style, previewCSS }) => ({
    label,
    value: style,
    className: previewCSS
  }));

/**
 * The common chart properties GUI schema that shares to all map type.
 */
export default {
  schemaKey: 'RP_GAUGE_TYPE',
  groups: {
    /* general info */
    generalInfo: {
      title: () => localizer.getText('REPORT_PART_GAUGE_PROPS_SCHEMA_LABEL_GENERAL_INFO'),
      fields: {
        chartType: {
          factory: createSelectSchema,
          title: () => localizer.getText('REPORT_PART_MAP_CONSTANTS_MAP_TYPE'),
          value: 'getChartType',
          props: {
            get options() {
              return getMapOptions();
            }
          }
        },
        continent: {
          factory: createSelectSchema,
          title: () => localizer.getText('REPORT_PART_MAP_CONSTANTS_CONTINENT'),
          value: 'getValueByKey',
          isHidden: 'isHiddenContinent',
          props: {
            options: MapCollection.CONTINENTS
          }
        },
        country: {
          factory: createSelectSchema,
          title: () => localizer.getText('REPORT_PART_MAP_CONSTANTS_COUNTRY'),
          value: 'getValueByKey',
          isHidden: 'isHiddenCountry',
          props: {
            options: MapCollection.COUNTRIES
          }
        },
        state: {
          factory: createSelectSchema,
          title: () => localizer.getText('REPORT_PART_MAP_CONSTANTS_STATE'),
          value: 'getValueByKey',
          isHidden: 'isHiddenState',
          props: {
            options: 'getStateByCountry'
          },
          staticProps: {
            blankOption: true,
            get blankOptionText() {
              return localizer.getText('COMMON_LABEL_ALL');
            }
          }
        }
      }
    },
    /* map */
    map: {
      title: () => localizer.getText('COMMON_LABEL_MAP'),
      isHidden: 'isNotBuild',
      hiddenByState: true,
      fields: {
        izBorder: {
          factory: createPopupSettingSchema,
          title: () => localizer.getText('COMMON_LABEL_BORDER'),
          value: 'getBorder',
          props: {
            get reportPartType() {
              return REPORT_PART_TYPE.Map;
            },
            get title() {
              return localizer.getText('REPORT_PART_MAP_CONSTANTS_MAP_BORDER_SETTINGS');
            },
            allChartProps: 'getAllChartProps',
            hadSetting: 'hadSettingBorder',
            fnResetDefaultValue: 'fnResetBorderDefaultValue',
            loadValue: 'getBorder'
          },
          staticProps: {
            get popupTitle() {
              return localizer.getText('REPORT_PART_MAP_CONSTANTS_MAP_BORDER_SETTINGS');
            },
            popupContent: ChartBorderSettings
          }
        },
        izBackgroundColor: {
          factory: createPopupSettingSchema,
          title: () => localizer.getText('COLOR_STYLING_BACKGROUND_TOOLTIP'),
          value: 'getBackgroundColor',
          props: {
            get reportPartType() {
              return REPORT_PART_TYPE.Map;
            },
            get title() {
              return localizer.getText('REPORT_PART_MAP_CONSTANTS_MAP_BACKGROUND_COLOR_SETTINGS');
            },
            allChartProps: 'getAllChartProps',
            hadSetting: 'hadSettingBackgroundColor',
            fnResetDefaultValue: 'fnResetBackgroundColorDefaultValue',
            loadValue: 'getBackgroundColor'
          },
          staticProps: {
            get popupTitle() {
              return localizer.getText('REPORT_PART_MAP_CONSTANTS_MAP_BACKGROUND_COLOR_SETTINGS');
            },
            popupContent: ChartBackgroundColorSettings
          }
        },
        colorTheme: {
          factory: createColorPaletteSettingSchema,
          title: () => localizer.getText('COMMON_LABEL_COLOR_THEME'),
          value: 'getColorTheme',
          props: {
            get selectionPopupTitle() {
              return localizer.getText('COLORTHEME_POPUP_HEADER');
            },
            get defaultColorThemeLabel() {
              return localizer.getText('COLORPALETTE_NOTHEME');
            },
            displaySettingStatus: true,
            needMaskDefault: false,
            defaultColorPalette: null
          }
        }
      }
    },
    labels: {
      title: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_LABELS'),
      isHidden: 'isNotBuild',
      fields: {
        izLabelOrientation: {
          factory: createOrientationSettingsSchema,
          title: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_DATA_LABELS'),
          value: 'getValueByKey'
        },
        izHoverLabels: {
          factory: createCheckBoxSchema,
          title: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_HOVER_LABELS'),
          value: 'getValueByKey'
        },
        izMapLabel: {
          factory: createCheckBoxSchema,
          title: () => localizer.getText('REPORT_PART_MAP_CONSTANTS_SHOW_MAP_LABELS'),
          value: 'getValueByKey'
        }
      }
    },
    legends: {
      title: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_LEGENDS'),
      isHidden: 'isNotBuild',
      fields: {
        legendSettings: {
          factory: createPopupSettingSchema,
          title: () => localizer.getText('REPORTDESIGNER_LABEL_SETTINGS'),
          value: 'getLegendSettings',
          props: {
            get reportPartType() {
              return REPORT_PART_TYPE.Map;
            },
            get title() {
              return localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_CHART_LEGEND_SETTINGS');
            },
            allChartProps: 'getAllChartProps',
            chartType: 'getChartType',
            hadSetting: 'hadSettingLegend',
            fnResetDefaultValue: 'fnResetLegendDefaultValue',
            loadValue: 'getLegendSettings'
          },
          staticProps: {
            get title() {
              return localizer.getText('REPORT_PART_MAP_CONSTANTS_MAP_LEGEND_SETTINGS');
            },
            get popupTitle() {
              return localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_LEGEND_SETTINGS');
            },
            popupContent: ChartLegendSettings
          }
        }
      }
    },
    data: {
      title: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_DATA'),
      isHidden: 'isNotBuild',
      fields: {
        izValueLabel: {
          factory: createCheckBoxSchema,
          title: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_LABEL_SHOW_VALUE_LABELS'),
          value: 'getValueByKey'
        },
        izShowTooltip: {
          factory: createCheckBoxSchema,
          title: () => localizer.getText('REPORT_PART_PROPS_CHART_SCHEMA_SHOW_TOOLTIPS'),
          value: 'getValueByKey'
        }
      }
    },
    view: {
      title: () => localizer.getText('REPORT_PART_MAP_CONSTANTS_VIEW'),
      isHidden: 'isNotBuild',
      hiddenByState: true,
      fields: {
        zoomConfig: {
          factory: createPopupSettingSchema,
          title: () => localizer.getText('REPORT_PART_MAP_CONSTANTS_ZOOM'),
          value: 'getZoomConfig',
          props: {
            get reportPartType() {
              return REPORT_PART_TYPE.Map;
            },
            get title() {
              return localizer.getText('REPORT_PART_MAP_CONSTANTS_ZOOM_SETTINGS');
            },
            allChartProps: 'getAllChartProps',
            hadSetting: 'hadSettingZoom',
            fnResetDefaultValue: 'fnResetZoomDefaultValue',
            loadValue: 'getZoomConfig'
          },
          staticProps: {
            get popupTitle() {
              return localizer.getText('REPORT_PART_MAP_CONSTANTS_ZOOM_SETTINGS');
            },
            popupContent: ZoomForMapSettings,
            popupProps: {
              type: 'none',
              autoClose: false
            }
          }
        },
        dataRefreshInterval: {
          factory: createPopupSettingSchema,
          title: () => localizer.getText('COMMON_LABEL_DATA_REFRESH_INTERVAL_SETTING'),
          value: 'getValueByKey',
          props: {
            get reportPartType() {
              return REPORT_PART_TYPE.Map;
            },
            get title() {
              return localizer.getText('COMMON_LABEL_DATA_REFRESH_INTERVAL_SETTING');
            },
            hadSetting: 'hadSettingRefreshInterval',
            fnResetDefaultValue: 'fnResetRefreshIntervalDefaultValue',
            hiddenByState: true,
            loadValue: 'getDataRefreshInterval'
          },
          staticProps: {
            get popupTitle() {
              return localizer.getText('COMMON_LABEL_DATA_REFRESH_INTERVAL_SETTING');
            },
            popupContent: ChartDataRefreshSettings,
            popupProps: {
              type: 'none',
              autoClose: false
            }
          }
        }
      }
    }
  }
};



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartMap/Izenda.DefaultMapPropsDefinedSchema.js